import {
  Box,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
} from '@mui/material';
import React, { useState, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Clear, AddAPhoto } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useFoodAnalysis from 'src/hooks/useFoodAnalysis';
import { useSnackbar } from 'notistack';

const DonationUploadFile = ({
  value = [],
  onChange,
  error,
  onAnalysisComplete,
  useAIToFillDetails = false,
  shouldTriggerAnalysis = false,
  onAnalysisTriggered,
  context = {},
  invalidIndices = [],
  compact = false,
  sx = {},
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslation('translation');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { analyzeFoodImages } = useFoodAnalysis();
  const hasTriggeredRef = useRef(false);

  const invalidIndexSet = React.useMemo(
    () => new Set(invalidIndices),
    [invalidIndices]
  );

  const [previewUrls, setPreviewUrls] = useState([]);
  const { getRootProps, getInputProps, isDragReject, open } = useDropzone({
    onDrop: async (acceptedFiles) => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        return;
      }

      const newFiles = [...value, ...acceptedFiles];
      onChange?.(newFiles);
    },
    onDropRejected: (rejectedFiles) => {
      const error = rejectedFiles[0]?.errors?.[0]?.code;
      if (error == 'file-invalid-type') {
        enqueueSnackbar(t('form.errors.must-be-image'), { variant: 'error' });
      } else if (error == 'file-too-large') {
        enqueueSnackbar(t('form.errors.file-too-large'), { variant: 'error' });
      } else {
        enqueueSnackbar(t('form.errors.uploading-image'), { variant: 'error' });
      }
    },
    multiple: true,
    accept: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] },
    maxSize: 5242880, // 5MB
    noClick: true,
    noKeyboard: true,
  });

  const handleAnalyzeFoodImages = async (files = null) => {
    try {
      setIsAnalyzing(true);

      const filesToAnalyze = files || value;
      const actualFiles = filesToAnalyze.map((file) => {
        const isUrl = typeof file === 'string';
        return isUrl ? file : file.file || file;
      });

      const result = await analyzeFoodImages(actualFiles, context);

      if (result.success && onAnalysisComplete) {
        onAnalysisComplete(result.data);
      } else if (!result.success) {
        // Notify parent about the error
        if (onAnalysisComplete) {
          onAnalysisComplete({ error: result.error || 'Analysis failed' });
        }
      }
    } catch (error) {
      if (onAnalysisComplete) {
        onAnalysisComplete({ error: error.message || 'Analysis failed' });
      }
    } finally {
      setIsAnalyzing(false);
      // Always notify parent that analysis trigger has been handled
      onAnalysisTriggered?.();
    }
  };

  useEffect(() => {
    if (
      shouldTriggerAnalysis &&
      !hasTriggeredRef.current &&
      useAIToFillDetails &&
      value.length > 0 &&
      !isAnalyzing
    ) {
      hasTriggeredRef.current = true;
      handleAnalyzeFoodImages();
    }
  }, [shouldTriggerAnalysis]);

  useEffect(() => {
    if (!shouldTriggerAnalysis) {
      hasTriggeredRef.current = false;
    }
  }, [shouldTriggerAnalysis]);

  useEffect(() => {
    if (!value || value.length === 0) {
      setPreviewUrls([]);
      return;
    }
    const urls = value.map((file) =>
      typeof file === 'string' ? file : URL.createObjectURL(file.file || file)
    );
    setPreviewUrls(urls);
    return () => {
      urls.forEach((url, idx) => {
        const file = value[idx];
        if (file && typeof file !== 'string') {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [value]);

  const handleRemove = (idx) => {
    const newFiles = [...value];
    newFiles.splice(idx, 1);
    onChange(newFiles);
  };

  if (compact) {
    return (
      <Stack
        spacing={1.5}
        sx={{ minWidth: 0, minHeight: 0, height: '100%', ...sx }}
      >
        <Box
          onClick={open}
          sx={{
            borderRadius: 1.25,
            cursor: 'pointer',
            minWidth: 0,
            width: '100%',
            flexShrink: 0,
          }}
        >
          <Stack
            {...getRootProps()}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              border: (theme) =>
                `1px dashed ${
                  isDragReject || error
                    ? theme.palette.error.main
                    : theme.palette.grey[300]
                }`,
              borderRadius: 1.25,
              px: 2,
              py: 1.5,
              backgroundColor: '#f7f9fa',
            }}
          >
            <input {...getInputProps()} />
            {isAnalyzing ? (
              <CircularProgress size={22} color="primary" />
            ) : (
              <AddAPhoto sx={{ fontSize: 22, color: 'primary.main' }} />
            )}
            <Stack minWidth={0}>
              <Typography variant="body2" fontWeight={600}>
                {t('donations.drop-or-browse')}
              </Typography>
              {isAnalyzing ? (
                <Typography variant="caption" color="primary">
                  {t('donations.analyzing-images')}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </Box>

        {value && value.length > 0 ? (
          <Box
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1,
              overflowY: 'auto',
              minHeight: 0,
              flex: 1,
              alignContent: 'flex-start',
            }}
          >
            {value.map((file, idx) => {
              const isUrl = typeof file === 'string';
              const preview = previewUrls[idx];
              const isInvalid = invalidIndexSet.has(idx);
              return (
                <Box
                  key={isUrl ? idx : file.name}
                  sx={{
                    position: 'relative',
                    width: 64,
                    height: 64,
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={preview}
                    alt={isUrl ? `image-${idx + 1}` : file.name || file.path}
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 1,
                      objectFit: 'cover',
                      border: (theme) =>
                        `1px solid ${
                          isInvalid
                            ? theme.palette.error.main
                            : theme.palette.grey[300]
                        }`,
                      backgroundColor: '#fafafa',
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(idx);
                    }}
                    sx={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 22,
                      height: 22,
                      backgroundColor: 'background.paper',
                      boxShadow: 1,
                      '&:hover': { backgroundColor: 'background.paper' },
                    }}
                  >
                    <Clear sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        ) : null}

        {error ? (
          <Typography variant="caption" color="error" display="block">
            {error}
          </Typography>
        ) : null}
      </Stack>
    );
  }

  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Box
        onClick={open}
        sx={{ borderRadius: 2, cursor: 'pointer', minWidth: 0, width: '100%' }}
      >
        <Stack
          {...getRootProps()}
          sx={{
            border: (theme) =>
              `1px dashed ${
                isDragReject || error
                  ? theme.palette.error.main
                  : theme.palette.grey[300]
              }`,
            borderRadius: 2,
            p: compact ? { xs: 1.5, sm: 2 } : { xs: 2, sm: 4 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: compact ? 168 : 220,
            backgroundColor: '#f7f9fa',
            transition: 'border-color 0.2s',
            position: 'relative',
          }}
        >
          <input {...getInputProps()} />
          <Stack
            direction="row"
            alignItems="center"
            spacing={compact ? 2 : 3}
            width="100%"
            minWidth={0}
          >
            <Box
              sx={{
                flexShrink: 0,
                border: (theme) =>
                  `2px dashed ${
                    isDragReject || error
                      ? theme.palette.error.main
                      : theme.palette.secondary.main
                  }`,
                borderRadius: '50%',
                p: compact ? 0.75 : 1,
              }}
            >
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  borderRadius: '50%',
                  p: compact ? 2.5 : 5,
                  background: '#fff',
                  display: 'flex',
                }}
              >
                {isAnalyzing ? (
                  <CircularProgress size={compact ? 32 : 40} color="primary" />
                ) : (
                  <AddAPhoto
                    sx={{
                      fontSize: compact ? 32 : 40,
                      color: 'primary.main',
                    }}
                  />
                )}
              </Stack>
            </Box>
            <Stack alignItems="start" flex={1} minWidth={0}>
              <Typography
                variant="h6"
                fontWeight={700}
                color="text.primary"
                sx={{ maxWidth: '100%', overflowWrap: 'anywhere' }}
              >
                {t('donations.select-files')}
              </Typography>
              <Typography
                mt={1}
                variant="body2"
                color="text.secondary"
                sx={{ maxWidth: '100%', overflowWrap: 'anywhere' }}
              >
                {t('donations.drop-files-or')}{' '}
                <span
                  style={{
                    color: '#1db954',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                  onClick={open}
                  tabIndex={0}
                  role="button"
                >
                  {t('donations.click-here')}
                </span>{' '}
                {t('donations.to-browse-through-your-machine')}{' '}
                {t('donations.additional-instructions')}
              </Typography>

              {isAnalyzing && (
                <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                  {t('donations.analyzing-images')}
                </Typography>
              )}

              {value && value.length > 0 && (
                <Stack
                  direction="column"
                  width="100%"
                  spacing={1}
                  mt={2}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{ cursor: 'auto', minWidth: 0 }}
                >
                  {value.map((file, idx) => {
                    const isUrl = typeof file === 'string';
                    const preview = previewUrls[idx];
                    const isInvalid = invalidIndexSet.has(idx);
                    return (
                      <Stack
                        key={isUrl ? idx : file.name}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                        sx={{
                          backgroundColor: '#fff',
                          border: (theme) =>
                            `1px solid ${
                              isInvalid
                                ? theme.palette.error.main
                                : theme.palette.grey[300]
                            }`,
                          borderRadius: 1,
                          pl: 2,
                          pr: 1,
                          boxSizing: 'border-box',
                          minWidth: 0,
                          maxWidth: '100%',
                          width: '100%',
                          pointerEvents: 'auto',
                        }}
                      >
                        <Box
                          component="img"
                          src={preview}
                          alt={
                            isUrl ? `image-${idx + 1}` : file.name || file.path
                          }
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 1,
                            objectFit: 'cover',
                            flexShrink: 0,
                            border: (theme) =>
                              `1px solid ${
                                isInvalid
                                  ? theme.palette.error.main
                                  : theme.palette.grey[300]
                              }`,
                            backgroundColor: '#fafafa',
                          }}
                        />
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                          sx={{ flexGrow: 1, minWidth: 0 }}
                        >
                          <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                            >
                              {isUrl
                                ? t('donations.placeholder-image', {
                                    index: idx + 1,
                                  })
                                : file.name || file.path}
                            </Typography>
                            {isInvalid && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ whiteSpace: 'normal' }}
                              >
                                {t('form.errors.corrupted-image-message')}
                              </Typography>
                            )}
                          </Stack>
                          <IconButton
                            sx={{ flexShrink: 0 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemove(idx);
                            }}
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
      {error && (
        <Typography variant="caption" color="error" display="block" mt={1}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DonationUploadFile;
